import type { DemoPill } from './colimind-types';

const SESSION = 'demo-local';

export const TREND_PILLS: DemoPill[] = [
  {
    label: 'CPI Trend',
    question: 'Show me the CPI trend over the last 30 days',
    response: {
      session_id: SESSION,
      conversation_id: SESSION,
      answer_text:
        'The **CPI** on Tenant A has been trending between **77 %** and **93 %** over the last 30 days, with a peak on May 11.',
      sql_query:
        "SELECT DATE_TRUNC('day', timestamp_utc) AS day, AVG(cpi) AS avg_cpi FROM kpi_snapshots GROUP BY day ORDER BY day",
      sources: [{ table: 'kpi_snapshots', description: 'kpi_snapshots' }],
      query_result: {
        columns: [
          { name: 'day', type_name: 'TIMESTAMP' },
          { name: 'avg_cpi', type_name: 'DOUBLE' },
        ],
        data_array: [
          ['2026-05-05T00:00:00Z', '0.821'],
          ['2026-05-06T00:00:00Z', '0.845'],
          ['2026-05-07T00:00:00Z', '0.813'],
          ['2026-05-08T00:00:00Z', '0.867'],
          ['2026-05-09T00:00:00Z', '0.832'],
          ['2026-05-10T00:00:00Z', '0.879'],
          ['2026-05-11T00:00:00Z', '0.931'],
          ['2026-05-12T00:00:00Z', '0.891'],
          ['2026-05-13T00:00:00Z', '0.856'],
          ['2026-05-14T00:00:00Z', '0.842'],
          ['2026-05-15T00:00:00Z', '0.819'],
          ['2026-05-16T00:00:00Z', '0.804'],
          ['2026-05-17T00:00:00Z', '0.827'],
          ['2026-05-18T00:00:00Z', '0.851'],
          ['2026-05-19T00:00:00Z', '0.838'],
          ['2026-05-20T00:00:00Z', '0.866'],
          ['2026-05-21T00:00:00Z', '0.873'],
          ['2026-05-22T00:00:00Z', '0.849'],
          ['2026-05-23T00:00:00Z', '0.825'],
          ['2026-05-24T00:00:00Z', '0.812'],
          ['2026-05-25T00:00:00Z', '0.834'],
          ['2026-05-26T00:00:00Z', '0.858'],
          ['2026-05-27T00:00:00Z', '0.871'],
          ['2026-05-28T00:00:00Z', '0.889'],
          ['2026-05-29T00:00:00Z', '0.901'],
          ['2026-05-30T00:00:00Z', '0.877'],
          ['2026-05-31T00:00:00Z', '0.863'],
          ['2026-06-01T00:00:00Z', '0.848'],
          ['2026-06-02T00:00:00Z', '0.856'],
          ['2026-06-03T00:00:00Z', '0.871'],
        ],
        row_count: 30,
      },
    },
  },
  {
    label: 'CPI per Line',
    question: 'Show me average CPI per production line',
    response: {
      session_id: SESSION,
      conversation_id: SESSION,
      answer_text:
        'Average CPI per production line — **Line A** leads with **89.2 %**, while **Line D** shows the lowest performance at **76.4 %**.',
      sql_query:
        'SELECT line, AVG(cpi) AS avg_cpi FROM kpi_snapshots GROUP BY line ORDER BY avg_cpi DESC',
      sources: [{ table: 'kpi_snapshots', description: 'kpi_snapshots' }],
      query_result: {
        columns: [
          { name: 'line', type_name: 'STRING' },
          { name: 'avg_cpi', type_name: 'DOUBLE' },
        ],
        data_array: [
          ['Line A', '0.892'],
          ['Line B', '0.874'],
          ['Line C', '0.851'],
          ['Line D', '0.764'],
          ['Line E', '0.831'],
          ['Line F', '0.858'],
        ],
        row_count: 6,
      },
    },
  },
  {
    label: 'CPI per Line & Site',
    question: 'Show me average CPI per line and site',
    response: {
      session_id: SESSION,
      conversation_id: SESSION,
      answer_text:
        'Average CPI broken down by production line and site. **Tenant A / Line A** shows the best performance at **91.3 %**.',
      sql_query:
        'SELECT line, site, AVG(cpi) AS avg_cpi FROM kpi_snapshots GROUP BY line, site ORDER BY site, line',
      sources: [{ table: 'kpi_snapshots', description: 'kpi_snapshots' }],
      query_result: {
        columns: [
          { name: 'line', type_name: 'STRING' },
          { name: 'site', type_name: 'STRING' },
          { name: 'avg_cpi', type_name: 'DOUBLE' },
        ],
        data_array: [
          ['Line A', 'Tenant A', '0.913'],
          ['Line B', 'Tenant A', '0.881'],
          ['Line C', 'Tenant A', '0.862'],
          ['Line A', 'Tenant B', '0.847'],
          ['Line B', 'Tenant B', '0.823'],
          ['Line C', 'Tenant B', '0.798'],
          ['Line A', 'Tenant C', '0.871'],
          ['Line B', 'Tenant C', '0.855'],
          ['Line C', 'Tenant C', '0.839'],
        ],
        row_count: 9,
      },
    },
  },
  {
    label: 'Weekly Trend all Lines',
    question: 'Show me the weekly CPI trend for all lines',
    response: {
      session_id: SESSION,
      conversation_id: SESSION,
      answer_text:
        'Weekly CPI trend across all production lines. **Line A** consistently outperforms, while **Line D** shows a recovery trend in recent weeks.',
      sql_query:
        "SELECT DATE_TRUNC('week', timestamp_utc) AS week, AVG(cpi) AS avg_cpi, STDDEV(cpi) AS stddev_cpi FROM kpi_snapshots GROUP BY week ORDER BY week",
      sources: [{ table: 'kpi_snapshots', description: 'kpi_snapshots' }],
      query_result: {
        columns: [
          { name: 'week', type_name: 'TIMESTAMP' },
          { name: 'avg_cpi', type_name: 'DOUBLE' },
          { name: 'stddev_cpi', type_name: 'DOUBLE' },
        ],
        data_array: [
          ['2026-04-06T00:00:00Z', '0.831', '0.041'],
          ['2026-04-13T00:00:00Z', '0.847', '0.038'],
          ['2026-04-20T00:00:00Z', '0.839', '0.044'],
          ['2026-04-27T00:00:00Z', '0.858', '0.036'],
          ['2026-05-04T00:00:00Z', '0.862', '0.039'],
          ['2026-05-11T00:00:00Z', '0.891', '0.033'],
          ['2026-05-18T00:00:00Z', '0.843', '0.042'],
          ['2026-05-25T00:00:00Z', '0.868', '0.037'],
        ],
        row_count: 8,
      },
    },
  },
];
